<template>
  <button
    :class="buttonClasses"
    :type="type"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <svg
      v-if="loading"
      class="intercom-btn-spinner"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
    >
      <circle cx="12" cy="12" r="10" opacity="0.25" />
      <path
        d="M12 2a10 10 0 0 1 10 10"
        opacity="0.75"
        stroke-linecap="round"
      />
    </svg>
    <slot v-if="!loading" />
    <span v-if="loading && loadingText">{{ loadingText }}</span>
  </button>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  /**
   * Button variant
   * @values primary, secondary, ghost
   */
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'ghost'].includes(value),
  },
  /**
   * Button size
   * @values sm, md, lg
   */
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value),
  },
  /**
   * HTML button type
   * @values button, submit, reset
   */
  type: {
    type: String,
    default: 'button',
  },
  /**
   * Disabled state
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Loading state
   */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * Loading text (shown when loading is true)
   */
  loadingText: {
    type: String,
    default: '',
  },
  /**
   * Full width button
   */
  block: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click'])

const buttonClasses = computed(() => [
  'intercom-btn',
  `intercom-btn--${props.variant}`,
  `intercom-btn--${props.size}`,
  {
    'intercom-btn--block': props.block,
    'intercom-btn--loading': props.loading,
    'intercom-btn--disabled': props.disabled,
  },
])

const handleClick = (event) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}
</script>

<style scoped>
/* ========================================
   INTERCOM BUTTON BASE
   ======================================== */
.intercom-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  font-family: var(--font-family);
  font-weight: var(--font-weight-semibold);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-base);
  border: none;
  outline: none;
  text-decoration: none;
  white-space: nowrap;
}

.intercom-btn:focus-visible {
  box-shadow: var(--shadow-focus);
}

/* ========================================
   SIZES
   ======================================== */
.intercom-btn--sm {
  padding: 8px 20px;
  font-size: var(--font-size-base);
  min-height: 36px;
}

.intercom-btn--md {
  padding: var(--space-2) var(--space-5);
  font-size: var(--font-size-base);
  height: 40px;
}

.intercom-btn--lg {
  padding: var(--space-3) var(--space-6);
  font-size: var(--font-size-md);
  height: 48px;
}

/* ========================================
   PRIMARY VARIANT (Black Button)
   ======================================== */
.intercom-btn--primary {
  background: var(--color-primary);
  color: var(--color-white);
  box-shadow: var(--shadow-sm);
}

.intercom-btn--primary:hover:not(:disabled):not(.intercom-btn--loading) {
  background: var(--color-primary-hover);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.intercom-btn--primary:active:not(:disabled):not(.intercom-btn--loading) {
  transform: translateY(0);
  box-shadow: var(--shadow-sm);
}

/* ========================================
   SECONDARY VARIANT (White with Border)
   ======================================== */
.intercom-btn--secondary {
  background: var(--color-white);
  color: var(--color-gray-700);
  border: 1px solid var(--color-gray-300);
  box-shadow: var(--shadow-xs);
}

.intercom-btn--secondary:hover:not(:disabled):not(.intercom-btn--loading) {
  background: var(--color-gray-100);
  border-color: var(--color-gray-400);
  box-shadow: var(--shadow-sm);
}

.intercom-btn--secondary:active:not(:disabled):not(.intercom-btn--loading) {
  background: var(--color-gray-200);
  box-shadow: var(--shadow-xs);
}

/* ========================================
   GHOST VARIANT (Transparent)
   ======================================== */
.intercom-btn--ghost {
  background: transparent;
  color: var(--color-gray-700);
  border: none;
}

.intercom-btn--ghost:hover:not(:disabled):not(.intercom-btn--loading) {
  background: var(--color-gray-100);
  color: var(--color-charcoal);
}

.intercom-btn--ghost:active:not(:disabled):not(.intercom-btn--loading) {
  background: var(--color-gray-200);
}

/* ========================================
   STATES
   ======================================== */
.intercom-btn--disabled,
.intercom-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.intercom-btn--loading {
  cursor: wait;
}

.intercom-btn--block {
  width: 100%;
}

/* ========================================
   LOADING SPINNER
   ======================================== */
.intercom-btn-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
